<template>
  <li
    class="side-nav-item"
    :class="{
      'mm-active': isActive,
      'mm-child-active': isActive && props.isChild,
      'side-nav-item-flex': isFlexRetailer === true
    }"
  >
    <template v-if="item.child">
      <a
        data-bs-toggle="collapse"
        :href="'#sidebarDashboards' + item.name"
        :aria-expanded="isActive"
        :aria-controls="'sidebarDashboards' + item.name"
        class="side-nav-link hover-side-nav-link-bg-with-child"
        @click.stop="toggleItem"
        @dblclick.stop.prevent=""
      >
        <div class="link-bg-with-child-wrapper">
          <i :class="item.icon" />
          <span v-if="item.child" class="menu-arrow" @click.stop="arrowToggleItem" @dblclick.stop.prevent=""></span>
          <span class="pointer-none">{{ item.text }}</span>
        </div>
      </a>
      <div class="collapse" :id="'sidebarDashboards' + item.name" :class="{ show: isActive }">
        <List
          :listItems="item.child"
          itemClass="side-nav-second-level mm-collapse pt-0"
          :class="{ 'mm-show': isActive }"
          :isChild="true"
        />
      </div>
    </template>
    <template v-else>
      <router-link
        :to="{ name: item.name }"
        class="side-nav-link d-flex text-decoration-none justify-content-between"
        @click.stop="handleEvent"
      >
        <div class="hover-side-nav-link-bg d-flex align-items-center">
          <div class="icon-container">
            <i :class="icon"></i>
            <span class="noti-icon-badge" v-show="isBadgeCountVisible === true"></span>
          </div>
          <span>
            {{ item.text }}
          </span>
        </div>
        <div v-if="isBadgeVisible === true || isBadgeCountVisible === true" class="d-flex align-items-center">
          <div class="d-flex">
            <span v-if="isBadgeVisible === true" class="badge p-1 text-white ms-1 flex" :class="item.badge.class">
              {{ item.badge.text }}
            </span>
            <span v-if="isBadgeCountVisible === true" class="badge num-badge ms-1 flex">
              {{ item.badgeCount }}
            </span>
          </div>
        </div>
      </router-link>
    </template>
  </li>
</template>

<script setup>
import { computed, ref } from 'vue'

import { storeToRefs } from 'pinia'

import { useSideMenuStore } from '@/stores/helpers/sideMenu.js'

import Retailer from '@/utilities/retailerHelper.js'

import List from '@/components/List.vue'

// #region - Props
const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  index: {
    type: String,
    required: false,
    default: null
  },
  isChild: {
    type: Boolean,
    required: false,
    default: false
  }
})
// endregion

// #region - references
const lastClickTime = ref(0)
const arrowLastClickTime = ref(0)
const childMenuClicked = ref(false)
// endregion

// #region - Vuex state
const sideMenuStore = useSideMenuStore()
const { sidebarActiveMenuIndex, sidebarActive } = storeToRefs(sideMenuStore)
const isBadgeVisible = computed(() => props.item.badge && (props.item.badge.show?.() ?? true))
const isBadgeCountVisible = computed(() => props.item.badgeCount && props.item.badgeCount > 0)
const icon = computed(() => props.item.icon + (isBadgeCountVisible.value ? ' has-new-item' : ''))
// endregion

// #region - computed properties
const isActive = computed(() => hasActiveChild(props.item))

const isFlexRetailer = computed(() => Retailer.isFlex())

// endregion

// #region - Method definitions
const hasActiveChild = subItem => {
  if (subItem.name === sidebarActiveMenuIndex.value) {
    return true
  }

  if (subItem.child) {
    return subItem.child.some(childItem => hasActiveChild(childItem))
  }

  return false
}

const toggleItem = el => {
  sideMenuStore.setActiveMenuIndex(props.index)

  const currentTime = Date.now()
  const timeDifference = currentTime - lastClickTime.value

  // Detected a double-click, ignoring or handling it specifically.
  if (timeDifference < 500) {
    return
  }

  sidebarActive.value = false

  if (el.target.nextSibling) {
    el.target.nextSibling.classList.toggle('mm-show')
  }

  if (props.isChild === false) {
    if (sidebarActive.value === false) {
      localStorage.setItem('sidebarOpened', '1')
      document.body.className = 'sidebar-enable'
      document.body.setAttribute('data-leftbar-compact-mode', 'scrollable')
      sideMenuStore.setSidebarCollapseState(true)
    }
  } else {
    handleEvent(el)
  }
  lastClickTime.value = currentTime
}

const arrowToggleItem = el => {
  sideMenuStore.setActiveMenuIndex(props.index)

  const currentTime = Date.now()
  const timeDifference = currentTime - arrowLastClickTime.value

  // Detected a double-click, ignoring or handling it specifically.
  if (timeDifference < 500) {
    return
  }

  if (el.target.parentElement.parentElement) {
    el.target.parentElement.parentElement.classList.toggle('mm-active')
  }

  if (el.target.parentElement.nextSibling) {
    el.target.parentElement.nextSibling.classList.toggle('mm-show')
  }

  if (props.isChild === false) {
    if (sidebarActive.value === false) {
      localStorage.setItem('sidebarOpened', '1')
      document.body.className = 'sidebar-enable'
      document.body.setAttribute('data-leftbar-compact-mode', 'scrollable')
      sideMenuStore.setSidebarCollapseState(true)
    }
  }
  sideMenuStore.setSidebarActive(!childMenuClicked.value)

  arrowLastClickTime.value = currentTime
}

const handleEvent = element => {
  sideMenuStore.setActiveMenuIndex(props.index)
}
// endregion
</script>

<style scoped>
.num-badge {
  background-color: #ffeaed;
  color: #ea3e53;
}

.icon-container {
  position: relative;
  display: inline-block;
}

body[data-leftbar-theme='light'] .side-nav-item-flex.mm-active > a {
  color: var(--violet-700) !important;
}

body[data-leftbar-theme='light'] .side-nav-item-flex.mm-active > a::before {
  background-color: var(--violet-700);
}

body[data-leftbar-theme='light'] .side-nav-item-flex > .side-nav-link:hover,
body[data-leftbar-theme='light'] .side-nav-item-flex > .side-nav-link:focus {
  color: var(--violet-700);
}

body[data-leftbar-theme='light'] .side-nav-item-flex > .side-nav-link:active {
  background-color: var(--white);
  box-shadow: none;
  color: var(--violet-700) !important;
}

body[data-leftbar-theme='light'] .side-nav-item-flex > .side-nav-link:hover .hover-side-nav-link-bg {
  background-color: var(--white);
  box-shadow: none;
  color: var(--violet-700) !important;
}

/* An item that opens a sub menu wraps its label in an element of its own. */
body[data-leftbar-theme='light']
  .side-nav
  > .side-nav-item-flex
  > .side-nav-link:hover
  .link-bg-with-child-wrapper {
  background-color: var(--white);
  box-shadow: none;
  color: var(--violet-700) !important;
}

body[data-leftbar-theme='light'][data-leftbar-compact-mode='condensed']
  .side-nav
  .side-nav-item-flex:hover
  > .side-nav-link {
  background: var(--white);
}

/* The condensed bar repaints the icon through a rule the one above only ties with, so it takes a
   selector of its own to settle rather than leaving the winner to stylesheet order. */
body[data-leftbar-compact-mode='condensed']
  .side-nav
  .side-nav-item-flex
  > .side-nav-link:hover
  .hover-side-nav-link-bg {
  color: var(--violet-700) !important;
}

body[data-leftbar-compact-mode='condensed'] .icon-container .has-new-item::after {
  content: '';
  position: absolute;
  top: 0;
  left: 15px;
  width: 12px;
  height: 12px;
  background-color: #e94f61;
  border-radius: 50%;
  border: 2px solid white;
}
</style>
